// @ts-strict-ignore
import { MBIconV2Name } from '@shared/components/image';
import { isLowCodeViewTemplate } from '@shared/scenes/cards/collection/components/isLowCodeViewTemplate';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { GenericComponent } from '../config';
import {
  isAuditComponent,
  isDateComponent,
  isEntityBatchComponent,
  isInputComponent,
  isTimeTrackerComponent,
} from '../utils';
import {
  STATIC_COMPONENT_IDS,
  getIconForComponentType,
  getLabelForComponentType,
} from './ComponentOptionDefaults';
import { ComponentTemplateSection } from './ComponentTemplateSection';
import { OptionsData } from './options/OptionsData';

const NO_CODE_VIEW_SUBTYPES: API.ViewSubtype[] = ['LIST', 'TABLE'];

export interface ComponentTemplatesParameters {
  templateSections: Array<ComponentTemplateSection>;
  reservedIds: Array<string>;
  nonEditableLabelComponentIds: Array<string>;
  nonEditablePlaceholderComponentIds: Array<string>;
}

export class ComponentTemplates {
  readonly templateSections: Array<ComponentTemplateSection>;
  private readonly reservedIds: Array<string>;
  private readonly nonEditableLabelComponentIds: Array<string>;
  private readonly nonEditablePlaceholderComponentIds: Array<string>;

  constructor(parameters: ComponentTemplatesParameters) {
    this.templateSections = parameters.templateSections;
    this.reservedIds = parameters.reservedIds;
    this.nonEditableLabelComponentIds = parameters.nonEditableLabelComponentIds;
    this.nonEditablePlaceholderComponentIds = parameters.nonEditablePlaceholderComponentIds;
  }

  private filterOutComponentsFromSections(existingComponents: GenericComponent[]) {
    const sectionsWithComponentsFiltered: Array<ComponentTemplateSection> = [];

    for (let section of this.templateSections) {
      const filteredOptions = section.items.filter(option => {
        return option.canBeAdded(existingComponents);
      });
      sectionsWithComponentsFiltered.push({
        title: section.title,
        items: filteredOptions,
        subformsOnly: section.subformsOnly,
      });
    }
    return sectionsWithComponentsFiltered;
  }

  private updateDependencies(existingComponents: GenericComponent[]) {
    for (let section of this.templateSections) {
      section.items.forEach(option => {
        option.updateMissingDependencies?.(existingComponents);
      });
    }
  }

  optionsDataForComponent(genericComponent: GenericComponent): OptionsData {
    if (isSpecialCaseComponent(genericComponent)) {
      return getSpecialCaseOptions(genericComponent);
    }

    const fieldTemplateOptions = this.templateSections.map(({ items }) => items).flat();

    for (let item of fieldTemplateOptions) {
      if (item.matchesComponent(genericComponent, this.reservedIds)) {
        return {
          title: item.title,
          icon: item.icon,
        };
      }
    }

    if (this.reservedIds.includes(genericComponent.id) && isInputComponent(genericComponent)) {
      return {
        title: genericComponent.label,
        icon: genericComponent.icon as MBIconV2Name,
      };
    }

    console.error('Could not find component in templates', genericComponent);
    return {
      title: 'Unknown',
      icon: 'multi-line',
    };
  }

  isComponentVisibilityDisabled(
    component: API.AnyComponent,
    viewTemplate: API.ViewTemplateFragment | undefined,
  ): boolean {
    const viewSubType = viewTemplate?.subtype;

    if (isTimeTrackerComponent(component)) {
      return !NO_CODE_VIEW_SUBTYPES.includes(viewSubType);
    }
    if (STATIC_COMPONENT_IDS.NAME === component.id) {
      return true;
    }
    if (viewSubType === 'CALENDAR' && isLowCodeViewTemplate({ viewTemplate })) {
      if (
        [STATIC_COMPONENT_IDS.ASSIGNEES, STATIC_COMPONENT_IDS.START_DATE].includes(component.id)
      ) {
        return false;
      } else if (component.type === 'TAG') {
        return false;
      }
      return true;
    }
    return false;
  }

  isLabelEditable(genericComponent: GenericComponent): boolean {
    return !this.nonEditableLabelComponentIds.includes(genericComponent.id);
  }

  isPlaceholderEditable(genericComponent: GenericComponent): boolean {
    const isAudit = isAuditComponent(genericComponent);
    const isServerManaged = genericComponent.isServerManaged;
    return (
      !isAudit &&
      !isServerManaged &&
      !this.nonEditablePlaceholderComponentIds.includes(genericComponent.id)
    );
  }

  isSubtypeEditable(genericComponent: GenericComponent): boolean {
    return !genericComponent.isServerManaged;
  }

  sectionsToDisplay(isSubform: boolean, existingComponents: GenericComponent[]) {
    this.updateDependencies(existingComponents);

    return this.filterOutComponentsFromSections(existingComponents).filter(({ subformsOnly }) =>
      isSubform ? subformsOnly : !subformsOnly,
    );
  }
}

function isSpecialCaseComponent(genericComponent: GenericComponent) {
  if (isDateComponent(genericComponent) && genericComponent.allowEntityBatchCreation) {
    return true;
  }
  if (isEntityBatchComponent(genericComponent)) {
    return true;
  }
  return false;
}

interface SpecialCaseOptions {
  title: string;
  icon: MBIconV2Name;
}

function getSpecialCaseOptions(genericComponent: GenericComponent): SpecialCaseOptions {
  if (isDateComponent(genericComponent) && genericComponent.allowEntityBatchCreation) {
    return {
      title: i18n('workflowTemplate.component.label.batchDate'),
      icon: getIconForComponentType('DATE'),
    };
  }
  if (isEntityBatchComponent(genericComponent)) {
    return {
      title: getLabelForComponentType('ENTITY_BATCH'),
      icon: getIconForComponentType('ENTITY_BATCH'),
    };
  }
  return {
    title: 'Unknown',
    icon: 'multi-line',
  };
}
